import { motion } from 'framer-motion'
import { useSafeTranslation } from '@/hooks/use-safe-translation'
import { LayoutGrid } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/Components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select'
import useLocalizedValue from '@/hooks/use-localized-value'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import {
  filterPartners,
  provincesOf,
  sortPartners,
  SORT_DISCOUNT,
  type Partner,
  type PartnerType,
  type Province,
} from '@/lib/vip-card'
import { MorphCards } from '@/Components/ui/morph-card'
import { PartnerCard } from './partner-card'
import { Reveal } from './reveal'
import { Swap } from './swap'

interface PartnersTabProps {
  partners: Partner[]
  types: PartnerType[]
}

const ALL = 'all'

/** Three rows, whatever the device shows in one: 3, 2 or 1 card wide. */
function usePageSize() {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const isTablet = useMediaQuery('(min-width: 768px)')

  if (isDesktop) {
    return 9
  }

  return isTablet ? 6 : 3
}

export function PartnersTab({ partners, types }: PartnersTabProps) {
  const [typeId, setTypeId] = useState<number | null>(null)
  const [provinceCode, setProvinceCode] = useState<string>(ALL)
  const [page, setPage] = useState(1)

  const t = useSafeTranslation()
  const perPage = usePageSize()
  const provinces = useMemo(() => provincesOf(partners), [partners])

  const shown = useMemo(() => {
    const narrowed = filterPartners(partners, {
      typeId,
      provinceCode: provinceCode === ALL ? null : provinceCode,
    })

    return sortPartners(narrowed, SORT_DISCOUNT, 'en')
  }, [partners, typeId, provinceCode])

  const pages = Math.max(1, Math.ceil(shown.length / perPage))

  /* A narrower device fits fewer, so a page that no longer exists falls back. */
  useEffect(() => setPage((current) => Math.min(current, pages)), [pages])

  const visible = shown.slice((page - 1) * perPage, page * perPage)

  function narrow(apply: () => void) {
    apply()
    setPage(1)
  }

  return (
    <div className="flex flex-col gap-6">
      <Reveal order={6} className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3 py-1.5">
          {/* Outside the scroller, so it is always the row's left boundary. */}
          <TypeChip active={typeId === null} onClick={() => narrow(() => setTypeId(null))}>
            <LayoutGrid className="size-5" />
            {t('vipCard.all')}
          </TypeChip>

          {/* One row that scrolls and fades at its end; it never wraps. */}
          <div
            data-pill-scroller
            className="-mx-px -my-1.5 min-w-0 flex-1 overflow-x-auto px-px py-1.5 [-ms-overflow-style:none] [mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)] [scrollbar-width:none] rtl:[mask-image:linear-gradient(to_left,black_calc(100%-28px),transparent)] [&::-webkit-scrollbar]:hidden"
          >
            <div className="flex w-max flex-nowrap gap-3">
              {types.map((type) => (
                <TypeChipForType
                  key={type.id}
                  type={type}
                  active={typeId === type.id}
                  onClick={() => narrow(() => setTypeId(type.id))}
                />
              ))}
            </div>
          </div>
        </div>

        {provinces.length > 1 ? (
          <Select
            value={provinceCode}
            onValueChange={(value) => narrow(() => setProvinceCode(value))}
          >
            <SelectTrigger className="h-12 w-full shrink-0 rounded-full border-0 bg-card px-4 text-base font-semibold text-muted-foreground ring-1 ring-transparent hover:ring-border sm:w-[220px]">
              <SelectValue placeholder={t('vipCard.selectProvince')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('vipCard.allProvinces')}</SelectItem>
              {provinces.map((province) => (
                <ProvinceOption key={province.code} province={province} />
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </Reveal>

      {/*
        A full page is three rows at every breakpoint. The height is only held
        open while there is more than one page — otherwise a short list would
        leave the section mostly empty.
      */}
      <Swap order={7} id={`${typeId ?? 'all'}-${provinceCode}-${page}`}>
        <MorphCards
          className={cn(
            'grid grid-cols-1 content-start gap-5 md:grid-cols-2 lg:grid-cols-3',
            pages > 1 && 'min-h-[460px]'
          )}
        >
          {visible.map((partner) => (
            <PartnerCard
              key={partner.id}
              partner={partner}
              provinceCode={provinceCode === ALL ? null : provinceCode}
            />
          ))}
        </MorphCards>

        {shown.length === 0 ? (
          <p className="flex min-h-[200px] items-center justify-center text-muted-foreground">
            {t('vipCard.noPartners')}
          </p>
        ) : null}
      </Swap>

      {pages > 1 ? (
        <Reveal order={8}>
          <Pagination>
            <PaginationContent className="gap-1">
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page === 1}
                  className={cn(
                    'rounded-full transition-colors hover:text-primary',
                    page === 1 && 'pointer-events-none opacity-40'
                  )}
                  onClick={(event) => {
                    event.preventDefault()
                    setPage((current) => Math.max(1, current - 1))
                  }}
                />
              </PaginationItem>

              {Array.from({ length: pages }, (_, index) => index + 1).map((number) => (
                <PaginationItem key={number}>
                  <PaginationLink
                    href="#"
                    isActive={number === page}
                    className={cn(
                      'rounded-full transition-colors',
                      number === page
                        ? 'border-0 bg-primary/[0.06] text-primary dark:bg-primary/[0.22]'
                        : 'border-0 text-muted-foreground hover:text-foreground'
                    )}
                    onClick={(event) => {
                      event.preventDefault()
                      setPage(number)
                    }}
                  >
                    {number}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page === pages}
                  className={cn(
                    'rounded-full transition-colors hover:text-primary',
                    page === pages && 'pointer-events-none opacity-40'
                  )}
                  onClick={(event) => {
                    event.preventDefault()
                    setPage((current) => Math.min(pages, current + 1))
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </Reveal>
      ) : null}
    </div>
  )
}

function TypeChipForType({
  type,
  active,
  onClick,
}: {
  type: PartnerType
  active: boolean
  onClick: () => void
}) {
  const name = useLocalizedValue(type.name)

  return (
    <TypeChip active={active} onClick={onClick}>
      {type.icon ? <img src={type.icon} alt="" className="size-5 shrink-0" /> : null}
      {name}
    </TypeChip>
  )
}

function ProvinceOption({ province }: { province: Province }) {
  const name = useLocalizedValue(province.name)

  return <SelectItem value={String(province.code)}>{name}</SelectItem>
}

function TypeChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        revealChip(event.currentTarget)
        onClick()
      }}
      className={cn(
        'relative flex h-12 shrink-0 items-center justify-center gap-3 whitespace-nowrap rounded-full px-4 py-3',
        'bg-card text-base font-semibold ring-1 ring-transparent transition-colors duration-300',
        active ? 'text-primary' : 'text-muted-foreground hover:text-foreground hover:ring-border'
      )}
    >
      {active ? (
        <motion.span
          layoutId="vip-filter-pill"
          className="absolute inset-0 rounded-full bg-primary/[0.06] ring-1 ring-primary/25 dark:bg-primary/[0.22] dark:ring-primary/50"
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        />
      ) : null}
      <span className="relative z-10 flex items-center gap-3">{children}</span>
    </button>
  )
}

/** The width the scroller's mask fades over, so a chip must clear it to read. */
const PILL_FADE = 28

/**
 * Scrolls a chip fully clear of the fade.
 *
 * `scrollIntoView` stops as soon as the element is nominally in view, which
 * leaves the chip sitting under the mask. The arithmetic is on rects and the
 * scroll is relative, so it reads the same in both directions.
 */
function revealChip(chip: HTMLElement): void {
  const track = chip.closest<HTMLElement>('[data-pill-scroller]')

  if (!track) {
    return
  }

  const isRtl = getComputedStyle(track).direction === 'rtl'
  const chipBox = chip.getBoundingClientRect()
  const trackBox = track.getBoundingClientRect()

  /* The fade sits on the inline end, which is the left edge when RTL. */
  const pastEnd = isRtl
    ? trackBox.left + PILL_FADE - chipBox.left
    : chipBox.right - (trackBox.right - PILL_FADE)

  const pastStart = isRtl ? chipBox.right - trackBox.right : trackBox.left - chipBox.left

  if (pastEnd > 0) {
    track.scrollBy({ left: isRtl ? -pastEnd : pastEnd, behavior: 'smooth' })
  } else if (pastStart > 0) {
    track.scrollBy({ left: isRtl ? pastStart : -pastStart, behavior: 'smooth' })
  }
}
